import React from 'react';
import { TypeSectionBanner } from "../../@types/sectionTypes";
import { BANNER_TYPE } from "../../../globals/constants";
import SectionInnerPageBanner from "../../sections/common/banner";
import PolicyLayout from "../../components/PolicyLayout";

const AcceptableUsePolicyPage = () => {
    const bannerData: TypeSectionBanner = {
        title: "Acceptable Use Policy",
        crumb: "Acceptable Use Policy",
        image: "/assets/images/banner/1.jpg"
    };

    const sections = [
        { id: "introduction", title: "Introduction" },
        { id: "scope", title: "Scope of Application" },
        { id: "general-principles", title: "General Principles" },
        { id: "prohibited-activities", title: "Prohibited Activities" },
        { id: "specific-guidelines", title: "Specific Service Guidelines" },
        { id: "account-responsibilities", title: "Account Responsibilities" },
        { id: "data-privacy", title: "Data and Privacy Responsibilities" },
        { id: "reporting", title: "Reporting and Enforcement" },
        { id: "consequences", title: "Consequences of Violations" },
        { id: "contact", title: "Contact Information" }
    ];

    return (
        <div className="acceptable-use-policy-page">
            <SectionInnerPageBanner _data={bannerData} _type={BANNER_TYPE.COMPACT} />
            
            <PolicyLayout title="Acceptable Use Policy" sections={sections}>

                <div id="introduction" className="section">
                    <h2>1. Introduction</h2>
                    <p>
                        This Acceptable Use Policy ("AUP") governs your use of the services, applications, and platforms 
                        provided by Anathoth Technologies Ltd ("we," "our," or "us"). This policy is designed to ensure 
                        that our services are used responsibly and legally, protecting both our users and our business.
                    </p>
                    <p>
                        This AUP applies to all users of our services and supplements our 
                        <a href="/terms-of-service" className="text-primary">Terms of Service</a> and 
                        <a href="/privacy-policy" className="text-primary">Privacy Policy</a>.
                    </p>
                </div>

                <div id="scope" className="section">
                    <h2>2. Scope of Application</h2>
                    <p>This policy applies to all users of:</p>
                    <ul>
                        <li>Our websites and web applications</li>
                        <li>Mobile applications developed by us</li>
                        <li>Software solutions we provide</li>
                        <li>Cloud services and hosting platforms</li>
                        <li>Payment processing systems</li>
                        <li>API services and integrations</li>
                        <li>Support and consulting services</li>
                        <li>Training platforms and materials</li>
                    </ul>
                </div>

                <div id="general-principles" className="section">
                    <h2>3. General Principles</h2>
                    <p>All users must:</p>
                    <ul>
                        <li><strong>Comply with Laws:</strong> Follow all applicable local, national, and international laws</li>
                        <li><strong>Respect Others:</strong> Treat other users, our staff, and third parties with respect</li>
                        <li><strong>Protect Security:</strong> Not compromise the security or integrity of our services</li>
                        <li><strong>Use Responsibly:</strong> Use our services only for their intended purposes</li>
                        <li><strong>Maintain Accuracy:</strong> Provide truthful and accurate information</li>
                    </ul>
                </div>

                <div id="prohibited-activities" className="section">
                    <h2>4. Prohibited Activities</h2>
                    <div className="alert alert-danger">
                        <h5>⚠️ Strictly Prohibited</h5>
                        <p>The following activities are expressly forbidden and may result in immediate termination of services:</p>
                    </div>

                    <h3>4.1 Illegal Activities</h3>
                    <ul>
                        <li>Any activity that violates local, national, or international laws</li>
                        <li>Money laundering or terrorist financing</li>
                        <li>Fraud, identity theft, or impersonation</li>
                        <li>Distribution of illegal content or materials</li>
                        <li>Violation of export control laws</li>
                        <li>Tax evasion or regulatory violations</li>
                        <li>Gambling in jurisdictions where prohibited</li>
                        <li>Drug trafficking or illegal substance distribution</li>
                    </ul>

                    <h3>4.2 Security Violations</h3>
                    <ul>
                        <li><strong>Unauthorized Access:</strong> Attempting to access accounts, systems, or data without permission</li>
                        <li><strong>Hacking:</strong> Penetration testing, vulnerability scanning, or system exploitation</li>
                        <li><strong>Malware Distribution:</strong> Uploading or distributing viruses, worms, trojans, or other malicious code</li>
                        <li><strong>Data Breaches:</strong> Attempting to access, steal, or corrupt user data</li>
                        <li><strong>Password Attacks:</strong> Brute force attacks or credential stuffing</li>
                        <li><strong>Social Engineering:</strong> Phishing, pretexting, or other deceptive practices</li>
                        <li><strong>Denial of Service:</strong> DDoS attacks or resource exhaustion attempts</li>
                        <li><strong>Network Scanning:</strong> Port scanning or network reconnaissance</li>
                    </ul>

                    <h3>4.3 Abuse and Harassment</h3>
                    <ul>
                        <li><strong>Harassment:</strong> Bullying, stalking, or intimidating other users</li>
                        <li><strong>Hate Speech:</strong> Content promoting hatred based on race, religion, gender, or other characteristics</li>
                        <li><strong>Discrimination:</strong> Unfair treatment based on protected characteristics</li>
                        <li><strong>Threats:</strong> Making threats of violence or harm</li>
                        <li><strong>Doxxing:</strong> Publishing private information without consent</li>
                        <li><strong>Spam:</strong> Sending unsolicited messages or content</li>
                        <li><strong>Trolling:</strong> Deliberately provoking or disrupting discussions</li>
                    </ul>

                    <h3>4.4 Intellectual Property Violations</h3>
                    <ul>
                        <li><strong>Copyright Infringement:</strong> Using copyrighted material without permission</li>
                        <li><strong>Trademark Violation:</strong> Unauthorized use of trademarks or logos</li>
                        <li><strong>Patent Infringement:</strong> Using patented technology without license</li>
                        <li><strong>Trade Secret Theft:</strong> Misappropriating confidential business information</li>
                        <li><strong>Piracy:</strong> Distributing or using pirated software or content</li>
                        <li><strong>Reverse Engineering:</strong> Attempting to extract source code or proprietary algorithms</li>
                    </ul>

                    <h3>4.5 Spam and Unsolicited Communications</h3>
                    <ul>
                        <li><strong>Email Spam:</strong> Sending unsolicited commercial emails</li>
                        <li><strong>SMS Spam:</strong> Sending unsolicited text messages</li>
                        <li><strong>Social Media Spam:</strong> Posting repetitive or irrelevant content</li>
                        <li><strong>Comment Spam:</strong> Posting irrelevant comments or links</li>
                        <li><strong>Chain Letters:</strong> Distributing pyramid schemes or chain messages</li>
                        <li><strong>Automated Messaging:</strong> Using bots for unsolicited communications</li>
                    </ul>

                    <h3>4.6 Harmful Content</h3>
                    <ul>
                        <li><strong>Adult Content:</strong> Pornography or sexually explicit material (unless explicitly permitted)</li>
                        <li><strong>Violence:</strong> Graphic violence or content promoting harm</li>
                        <li><strong>Extremism:</strong> Content promoting terrorist or extremist ideologies</li>
                        <li><strong>Self-Harm:</strong> Content encouraging suicide or self-injury</li>
                        <li><strong>Misinformation:</strong> Deliberately false or misleading information</li>
                        <li><strong>Defamation:</strong> False statements damaging to reputation</li>
                    </ul>

                    <h3>4.7 Commercial Misuse</h3>
                    <ul>
                        <li><strong>Unauthorized Reselling:</strong> Reselling our services without permission</li>
                        <li><strong>Competitive Intelligence:</strong> Using our services to monitor competitors unfairly</li>
                        <li><strong>Scraping:</strong> Automated data extraction without permission</li>
                        <li><strong>API Abuse:</strong> Exceeding rate limits or misusing API endpoints</li>
                        <li><strong>Resource Abuse:</strong> Excessive use that impacts other users</li>
                        <li><strong>Cryptocurrency Mining:</strong> Using our infrastructure for mining operations</li>
                    </ul>
                </div>

                <div id="specific-guidelines" className="section">
                    <h2>5. Specific Service Guidelines</h2>
                    <h3>5.1 Web Applications</h3>
                    <ul>
                        <li>Do not attempt SQL injection or XSS attacks</li>
                        <li>Respect rate limits and usage quotas</li>
                        <li>Do not create fake accounts or profiles</li>
                        <li>Follow proper logout procedures</li>
                    </ul>

                    <h3>5.2 Mobile Applications</h3>
                    <ul>
                        <li>Do not attempt to reverse engineer the app</li>
                        <li>Respect device permissions and privacy settings</li>
                        <li>Do not distribute modified versions of our apps</li>
                        <li>Report security vulnerabilities responsibly</li>
                    </ul>

                    <h3>5.3 Payment Systems</h3>
                    <ul>
                        <li>Provide accurate payment information</li>
                        <li>Do not use stolen or fraudulent payment methods</li>
                        <li>Comply with PCI DSS requirements where applicable</li>
                        <li>Report suspicious transactions immediately</li>
                    </ul>

                    <h3>5.4 Cloud Services</h3>
                    <ul>
                        <li>Do not use services for illegal file hosting</li>
                        <li>Maintain appropriate backup strategies</li>
                        <li>Monitor resource usage and optimize as needed</li>
                        <li>Implement proper access controls</li>
                    </ul>
                </div>

                <div id="account-responsibilities" className="section">
                    <h2>6. Account Responsibilities</h2>
                    
                    <h3>Account Security</h3>
                    <p>Use strong, unique passwords and enable two-factor authentication when available. Keep login credentials confidential, report suspicious account activity immediately, and log out from shared or public devices.</p>
                    
                    <h3>Account Information</h3>
                    <p>Provide accurate registration information and keep account information up to date. Do not share accounts with unauthorized users and use your real identity (no impersonation).</p>
                </div>

                <div id="data-privacy" className="section">
                    <h2>7. Data and Privacy Responsibilities</h2>
                    
                    <h3>Personal Data</h3>
                    <p>Respect others' privacy rights and do not collect personal data without consent. Comply with applicable data protection laws, report data breaches immediately, and use data only for stated purposes.</p>
                    
                    <h3>Business Data</h3>
                    <p>Maintain confidentiality of sensitive information and implement appropriate security measures. Follow data retention policies and ensure proper data backups.</p>
                </div>

                <div id="reporting" className="section">
                    <h2>8. Reporting and Enforcement</h2>
                    <h3>8.1 Reporting Violations</h3>
                    <p>If you become aware of any violations of this AUP, please report them immediately:</p>
                    <ul>
                        <li><strong>Email:</strong> abuse@theanathoth.com</li>
                        <li><strong>Phone:</strong> +256 744 666608 (emergency situations)</li>
                        <li><strong>Security Issues:</strong> security@theanathoth.com</li>
                        <li><strong>General Support:</strong> info@theanathoth.com</li>
                    </ul>

                    <h3>8.2 Investigation Process</h3>
                    <p>Upon receiving a report, we will:</p>
                    <ol>
                        <li>Acknowledge receipt within 24 hours</li>
                        <li>Conduct a thorough investigation</li>
                        <li>Take appropriate enforcement action</li>
                        <li>Notify relevant parties of the outcome</li>
                        <li>Document the incident for future reference</li>
                    </ol>

                    <h3>8.3 Protection for Reporters</h3>
                    <ul>
                        <li>We protect the identity of those reporting violations</li>
                        <li>No retaliation against good-faith reporters</li>
                        <li>Anonymous reporting options available</li>
                        <li>Legal protection where applicable</li>
                    </ul>
                </div>

                <div id="consequences" className="section">
                    <h2>9. Consequences of Violations</h2>
                    <h3>9.1 Enforcement Actions</h3>
                    <p>Violations may result in one or more of the following actions:</p>
                    <ul>
                        <li><strong>Warning:</strong> First-time or minor violations</li>
                        <li><strong>Account Suspension:</strong> Temporary restriction of services</li>
                        <li><strong>Account Termination:</strong> Permanent closure of account</li>
                        <li><strong>Service Restriction:</strong> Limited access to specific features</li>
                        <li><strong>Legal Action:</strong> Referral to law enforcement if applicable</li>
                        <li><strong>Financial Penalties:</strong> Charges for damages or cleanup costs</li>
                    </ul>

                    <h3>9.2 Factors Considered</h3>
                    <p>Enforcement decisions are based on:</p>
                    <ul>
                        <li>Severity of the violation</li>
                        <li>Impact on other users or our systems</li>
                        <li>User's history of violations</li>
                        <li>Intent and circumstances</li>
                        <li>Legal requirements</li>
                        <li>Risk to business operations</li>
                    </ul>

                    <h3>9.3 Appeals Process</h3>
                    <p>If you believe enforcement action was taken in error:</p>
                    <ol>
                        <li>Submit an appeal to appeals@theanathoth.com</li>
                        <li>Provide detailed explanation and evidence</li>
                        <li>Our team will review within 5 business days</li>
                        <li>You will receive a written response</li>
                        <li>Additional review levels available for serious matters</li>
                    </ol>
                </div>

                <div id="contact" className="section">
                    <h2>10. Contact Information</h2>
                    <p>For questions about this Acceptable Use Policy or to report violations, feel free to contact us or drop us an email.</p>
                    <p><strong>Contact Information:</strong></p>
                    <ul>
                        <li><strong>Email:</strong> info@theanathoth.com</li>
                        <li><strong>Phone:</strong> +256 744 666608</li>
                        <li><strong>Address:</strong> 1 Kololo Hill Drive, Kampala, Uganda</li>
                    </ul>
                    <p><em>Last updated on 01 January, 2025</em></p>
                </div>
            </PolicyLayout>
        </div>
    );
};

export default AcceptableUsePolicyPage; 